import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { authFetch } from '../utils/authFetch';
import { 
  Card, Stack, Group, Text, Badge,
  RingProgress, Center, Box,
  Timeline, Divider, Alert, Button, Collapse, Transition
} from '@mantine/core';
import {
  IconTarget, IconAnalyze, IconSearch,
  IconRefresh, IconShieldCheck, IconRocket,
  IconBrain, IconZoomScan, IconUsers,
  IconAlertTriangle, IconCheck, IconHourglassHigh, IconChevronRight
} from '@tabler/icons-react';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
import PolicyEvidenceBadges from '../components/PolicyEvidenceBadges';
// RTK async thunks for autopilot operations
import {
  fetchRiskCardData,
  setAssessmentCheckpoint
} from '../redux/slices/assessmentsSlice';
import {
  setAreaStatus,
  updateAreaProgress,
  saveCheckpoint,
  markResumed,
  clearCheckpoint,
  selectResumeData
} from '../redux/slices/assessmentProgressSlice';
import { RiskDiscoveryServiceV2 } from './services/RiskDiscoveryServiceV2';
import { RiskEvaluationService } from './services/RiskEvaluationService';
import { ActionPlanningService } from './services/ActionPlanningService';
import { ResidualRiskService } from './services/ResidualRiskService';
import { SecurityRiskService } from './services/SecurityRiskService';
// Bridge selectors for gradual migration to assessment-centric state
import {
  selectContextBridge,
  selectSavedControlsBridge,
  selectSecurityBridge
} from '../redux/selectors/assessmentSelectors';
// createRiskFormSelector provides the authoritative risk taxonomy from assessment's template
// createImpactCategoriesSelector provides impact categories for risk evaluation (template-based)
import { createRiskFormSelector, createImpactCategoriesSelector } from '../redux/selectors/riskFormGenerator';

// Enhanced task definitions with beautiful icons and descriptions
const RISK_TASKS = [
  { 
    key: 'analyzing risk area', 
    label: 'Analyzing Risk Area', 
    icon: IconAnalyze, 
    color: '#7c3aed',
    description: 'Generating focused area analysis and summary'
  },
  { 
    key: 'researching risk sources', 
    label: 'Researching Risk Sources', 
    icon: IconZoomScan, 
    color: '#339af0',
    description: 'Evidence extraction for relevant risk sources'
  },
  { 
    key: 'researching root causes', 
    label: 'Researching Root Causes', 
    icon: IconSearch, 
    color: '#fd7e14',
    description: 'Research potential causes with evidence analysis'
  },
  { 
    key: 'preparing causes for RAG', 
    label: 'Preparing Causes for RAG', 
    icon: IconTarget, 
    color: '#20c997',
    description: 'Extracting relevant causes for enhanced scenario generation'
  },
  { 
    key: 'scenarios', 
    label: 'Generating Risk Scenarios', 
    icon: IconBrain, 
    color: '#e03131',
    description: 'Creating structured risk scenarios with RAG-enhanced analysis'
  },
  { 
    key: 'evaluation', 
    label: 'Evaluating Risk Priorities', 
    icon: IconAnalyze, 
    color: '#9c36b5',
    description: 'Assessing risk priority and rationale using AI'
  },
  { 
    key: 'actions', 
    label: 'Planning Actions', 
    icon: IconRocket, 
    color: '#37b24d',
    description: 'Developing mitigation strategies and evaluations'
  },
  {
    key: 'residual',
    label: 'Calculating Residual Risk',
    icon: IconShieldCheck,
    color: '#f76707',
    description: 'Determining remaining risk after controls'
  }
];

const progressForPhase = (phase) => {
  switch (phase) {
    case 'discovery':
      return 25;
    case 'evaluation':
      return 50;
    case 'actions':
      return 75;
    case 'residual':
      return 90;
    case 'completed':
      return 100;
    default:
      return 5;
  }
};

// Security assessment tasks (executed after all 6 risk areas)
// Currently tracked as single 'security' phase in statusByArea
// Individual task breakdown available for future UI enhancements
// const SECURITY_TASKS = [
//   { key: 'generating scenarios', label: 'Generating Threat Scenarios', icon: IconShield, color: '#7c3aed' },
//   { key: 'assessing threats', label: 'Assessing Threat Levels', icon: IconAnalyze, color: '#e03131' },
//   { key: 'generating controls', label: 'Generating Security Controls', icon: IconShieldCheck, color: '#37b24d' }
// ];

export default function RiskAutopilotV2({
  modelId,
  assessmentUuid,
  bucket,
  path,
  approvedNarrative,
  onComplete,
  onAreaProgress,
  autonomous = false, // New prop to control auto-progression
  assessmentMode = 'prompt' // 'prompt' (existing pipeline) or 'agentic' (orchestrator)
  // resumeFromPosition is now read from Redux via selectResumeData
}) {
  const dispatch = useDispatch();
  const { user } = useAuth0();

  // Read resume position from RTK slice (replaces prop)
  const resumeFromPosition = useSelector((state) => selectResumeData(state, assessmentUuid));

  // Track if required props are valid (checked in render, not as early return)
  const hasMissingUploadData = !bucket || !path;

  // DEBUG: Log upload data for troubleshooting
  useEffect(() => {
    console.log('[RiskAutopilotV2] Upload props:', { bucket, path, hasMissingUploadData });
  }, [bucket, path, hasMissingUploadData]);

  // Prefer assessment slice state for risk form data
  const assessmentEntry = useSelector((s) => assessmentUuid ? s.assessments?.byId?.[assessmentUuid] : null);
  const riskFormData = useMemo(() => assessmentEntry?.riskFormData || {}, [assessmentEntry?.riskFormData]);
  const assessmentMeta = useMemo(() => assessmentEntry || {}, [assessmentEntry]);
  const controlState = useSelector((s) => s.controls || {}); // Controls remain global
  const contextData = useSelector(selectContextBridge(assessmentUuid, modelId));
  const riskCauseEvalCfg = useSelector((s) => s.config?.configs?.riskCauseEvaluation); // Config is global
  const savedControls = useSelector(selectSavedControlsBridge(assessmentUuid, modelId));
  // Direct RTK slice access (no bridge) - assessment-centric residual risk state
  const residualRiskState = useSelector(
    (state) => state.residualRiskRTK?.byAssessment?.[assessmentUuid] || {}
  );
  const securityState = useSelector(selectSecurityBridge(assessmentUuid, modelId));

  // Create memoized selector for risk form taxonomy based on assessment's template
  // This is the SOURCE OF TRUTH for risk areas - derived from assessment's template_id
  const riskFormSelector = useMemo(() => createRiskFormSelector(assessmentUuid), [assessmentUuid]);
  const riskFormTaxonomy = useSelector(riskFormSelector);

  // Impact categories from template for risk evaluation (replaces legacy themes)
  const impactCategoriesSelector = useMemo(() => createImpactCategoriesSelector(assessmentUuid), [assessmentUuid]);
  const impactCategories = useSelector(impactCategoriesSelector);

  // DEBUG: Log riskFormData state
  useEffect(() => {
    console.log('[RiskAutopilotV2] DEBUG riskFormData:', {
      modelId,
      hasRiskFormData: !!riskFormData,
      riskFormDataKeys: riskFormData ? Object.keys(riskFormData) : [],
      riskFormDataEmpty: !riskFormData || Object.keys(riskFormData).length === 0,
      useCaseShortDescription: assessmentEntry?.useCaseShortDescription
    });
  }, [modelId, riskFormData, assessmentEntry?.useCaseShortDescription]);

  // DEBUG: Log autonomous prop changes
  useEffect(() => {
    console.log(`[RiskAutopilotV2] 🤖 AUTONOMOUS MODE PROP CHANGED: ${autonomous}`);
  }, [autonomous]);
  
  // Use ref to avoid stale closure issues with autonomous prop
  const autonomousRef = useRef(autonomous);
  useEffect(() => {
    autonomousRef.current = autonomous;
  }, [autonomous]);

  // Track if we've initialized to prevent re-initialization during execution
  const hasInitializedRef = useRef(false);

  // Refs to capture Redux state for initialization (avoids exhaustive-deps issues)
  const riskFormDataRef = useRef(riskFormData);
  const savedControlsRef = useRef(savedControls);
  const residualRiskStateRef = useRef(residualRiskState);
  const securityStateRef = useRef(securityState);

  // Update refs when Redux state changes
  useEffect(() => {
    riskFormDataRef.current = riskFormData;
    savedControlsRef.current = savedControls;
    residualRiskStateRef.current = residualRiskState;
    securityStateRef.current = securityState;
  }, [riskFormData, savedControls, residualRiskState, securityState]);

  const [currentAreaIndex, setCurrentAreaIndex] = useState(0);
  const [statusByArea, setStatusByArea] = useState({}); // areaName -> { currentTask, completedTasks, status }
  const [areaOutputs, setAreaOutputs] = useState({});
  const [error, setError] = useState(null);
  const [waitingForDecision, setWaitingForDecision] = useState(false);
  const [completedAreaData, setCompletedAreaData] = useState(null);

  // Derive areas from RiskFormGenerator taxonomy (SOURCE OF TRUTH for risk areas)
  // This ensures we have the 6 risk areas even for NEW assessments where riskFormData is empty
  // Falls back to riskFormData keys if taxonomy is empty (shouldn't happen normally)
  const areas = useMemo(() => {
    const taxonomyAreas = Object.keys(riskFormTaxonomy || {});
    const savedAreas = Object.keys(riskFormData || {});

    // Use taxonomy as primary source, fallback to saved data
    const riskAreas = taxonomyAreas.length > 0 ? taxonomyAreas : savedAreas;

    console.log('[RiskAutopilotV2] Areas derived:', {
      fromTaxonomy: taxonomyAreas.length,
      fromSaved: savedAreas.length,
      using: taxonomyAreas.length > 0 ? 'taxonomy' : 'saved',
      areas: riskAreas
    });

    return [...riskAreas, 'Security Assessment'];
  }, [riskFormTaxonomy, riskFormData]);
  
  // Helper function to safely parse and normalize API response data
  const normalizeSourceData = useCallback((rawData) => {
    if (!rawData) return [];
    
    // If it's already normalized, return as-is
    if (Array.isArray(rawData) && rawData[0]?.sourceName) {
      return rawData;
    }
    
    // If it's a raw API response, extract the actual data
    if (rawData.selectedSources) {
      return rawData.selectedSources;
    }
    
    // If it's an array of raw API responses, normalize each
    if (Array.isArray(rawData)) {
      return rawData.map(item => {
        if (typeof item === 'string') {
          try {
            const parsed = JSON.parse(item);
            return {
              sourceName: parsed.sourceName || 'Unknown Source',
              aiConfidence: parsed.aiConfidence || 0,
              evidenceSnippets: parsed.evidenceSnippets || [],
              rationale: parsed.rationale || 'Analysis completed',
              selected: parsed.selected !== false
            };
          } catch (e) {
            console.warn('[normalizeSourceData] Failed to parse:', item);
            return null;
          }
        }
        return item;
      }).filter(Boolean);
    }
    
    console.warn('[normalizeSourceData] Unexpected data structure:', rawData);
    return [];
  }, []);
  
  // Live update callback for real-time result streaming
  const handleLiveUpdate = useCallback((updateType, data) => {
    console.log(`[LiveUpdate] ${updateType}:`, data);
    setAreaOutputs((prev) => {
      const currentArea = areas[currentAreaIndex];
      if (!currentArea) return prev;
      
      const currentResults = prev[currentArea] || {};
      
      switch (updateType) {
        case 'area':
          // Area analysis complete
          return { 
            ...prev, 
            [currentArea]: { 
              ...currentResults, 
              areaCard: data.areaCard 
            } 
          };

        case 'sources':
          // Source research complete - normalize the data structure
          const normalizedSources = normalizeSourceData(data.selectedSources);
          return { 
            ...prev, 
            [currentArea]: { 
              ...currentResults, 
              selectedSources: normalizedSources
            } 
          };
        
        case 'causes':
          // Cause research complete
          return { 
            ...prev, 
            [currentArea]: { 
              ...currentResults, 
              causesBySource: data.causesBySource || {} 
            } 
          };

        case 'causesForRAG':
          // RAG cause preparation complete
          return { 
            ...prev, 
            [currentArea]: { 
              ...currentResults, 
              relevantCauses: data.relevantCauses || [],
              totalCauseCategories: data.totalCauseCategories || 0
            } 
          };

        case 'assignments':
          // Final assignments and evaluations complete
          return { 
            ...prev, 
            [currentArea]: { 
              ...currentResults, 
              finalAssignments: data.finalAssignments || [],
              evaluations: data.evaluations || []
            } 
          };
        case 'evaluations':
          return { ...prev, [currentArea]: { ...currentResults, evaluations: data.evaluations } };
        case 'scenarios':
          // RAG scenario generation complete with metadata
          return { 
            ...prev, 
            [currentArea]: { 
              ...currentResults, 
              scenarios: data.scenarios || [],
              ragMetadata: data.ragMetadata || {},
              scenarioStats: {
                totalGenerated: data.totalGenerated || data.scenarios?.length || 0,
                totalPromoted: data.totalPromoted || 0,
                generationMethod: data.ragMetadata?.method || 'Unknown',
                cataloguesUsed: data.ragMetadata?.cataloguesUsed || []
              }
            } 
          };
        case 'riskEvaluation':
          // Store individual risk evaluations as they complete
          const riskEvaluations = currentResults.riskEvaluations || [];
          return { ...prev, [currentArea]: { 
            ...currentResults, 
            riskEvaluations: [...riskEvaluations, data],
            evaluationProgress: `${data.evaluatedCount}/${data.totalRisks}`
          }};
        case 'controlsGenerated':
          // Store control generation updates
          const controlUpdates = currentResults.controlUpdates || [];
          return { ...prev, [currentArea]: { 
            ...currentResults, 
            controlUpdates: [...controlUpdates, data]
          }};
        case 'controlEvaluated':
          // Store individual control evaluations
          const controlEvaluations = currentResults.controlEvaluations || [];
          return { ...prev, [currentArea]: { 
            ...currentResults, 
            controlEvaluations: [...controlEvaluations, data]
          }};
        case 'actionPlanAnalyzed':
          // Store action plan analysis updates
          const actionAnalyses = currentResults.actionAnalyses || [];
          return { ...prev, [currentArea]: { 
            ...currentResults, 
            actionAnalyses: [...actionAnalyses, data]
          }};
        case 'residualRiskCalculated':
          // Store residual risk calculations
          const residualCalculations = currentResults.residualCalculations || [];
          return { ...prev, [currentArea]: {
            ...currentResults,
            residualCalculations: [...residualCalculations, data],
            residualProgress: `${data.assessedCount}/${data.totalRisks}`
          }};

        // Security assessment updates
        case 'scenariosGenerated':
          return {
            ...prev,
            security: {
              ...(prev.security || {}),
              scenarios: data.scenarios || [],
              scenarioCount: data.count || 0
            }
          };
        case 'scenarioEvaluated':
          const evaluatedScenarios = (prev.security?.evaluatedScenarios || []);
          return {
            ...prev,
            security: {
              ...(prev.security || {}),
              evaluatedScenarios: [...evaluatedScenarios, data],
              currentScenario: data.scenarioName,
              progress: `${data.scenarioIndex}/${data.totalScenarios}`
            }
          };
        case 'securityControlsGenerated':
          const generatedControls = (prev.security?.generatedControls || []);
          return {
            ...prev,
            security: {
              ...(prev.security || {}),
              generatedControls: [...generatedControls, data],
              totalControls: (prev.security?.totalControls || 0) + (data.controlsCount || 0)
            }
          };
        default:
          return prev;
      }
    });
  }, [areas, currentAreaIndex, normalizeSourceData]);

  // Initialize backend services with live update callback for ALL services
  const discoveryService = useMemo(() => new RiskDiscoveryServiceV2(dispatch, modelId, assessmentUuid, bucket, path, user, handleLiveUpdate), [dispatch, modelId, assessmentUuid, bucket, path, user, handleLiveUpdate]);
  const evaluationService = useMemo(() => new RiskEvaluationService(dispatch, modelId, assessmentUuid, contextData, impactCategories, handleLiveUpdate), [dispatch, modelId, assessmentUuid, contextData, impactCategories, handleLiveUpdate]);
  const actionService = useMemo(() => new ActionPlanningService(dispatch, modelId, assessmentUuid, handleLiveUpdate), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);
  const residualService = useMemo(() => new ResidualRiskService(dispatch, modelId, assessmentUuid, handleLiveUpdate), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);
  const securityService = useMemo(() => new SecurityRiskService(dispatch, modelId, assessmentUuid, handleLiveUpdate), [dispatch, modelId, assessmentUuid, handleLiveUpdate]);

  // Initialize queue with enhanced status tracking and resume support
  useEffect(() => {
    // Create unique key for this initialization
    const initKey = `${areas.join(',')}-${resumeFromPosition?.pausedAt || 'new'}`;

    // Only initialize once per unique configuration
    if (hasInitializedRef.current === initKey) return;
    if (!areas.length) return;

    hasInitializedRef.current = initKey;

    // Inline detection of completed areas (reads from refs to avoid exhaustive-deps)
    const detectCompleted = () => {
      const completed = [];
      const currentRiskFormData = riskFormDataRef.current;
      const currentSavedControls = savedControlsRef.current;
      const currentResidualRiskState = residualRiskStateRef.current;
      const currentSecurityState = securityStateRef.current;

      // Check 6 risk areas
      Object.keys(currentRiskFormData).forEach(areaName => {
        const areaRisks = currentRiskFormData[areaName]?.areaRisks || currentRiskFormData[areaName]?.sectionRisks || {};
        const hasRisks = Object.values(areaRisks).some(risks =>
          Array.isArray(risks) && risks.some(r => r.status === 'Saved')
        );

        if (hasRisks) {
          const hasEvaluations = Object.values(areaRisks).some(risks =>
            Array.isArray(risks) && risks.some(r => r.priority)
          );
          const hasActions = Object.values(areaRisks).some(risks =>
            Array.isArray(risks) && risks.some(r => currentSavedControls[r.riskId]?.length > 0)
          );
          const hasResidual = Object.values(areaRisks).some(risks =>
            Array.isArray(risks) && risks.some(r => currentResidualRiskState[r.riskId])
          );

          if (hasRisks && hasEvaluations && hasActions && hasResidual) {
            completed.push(areaName);
          }
        }
      });

      // Check security
      if (currentSecurityState.scenarios && Object.keys(currentSecurityState.scenarios).length > 0) {
        completed.push('Security Assessment');
      }

      return completed;
    };

    // Detect what's already completed based on Redux data
    const actuallyCompleted = detectCompleted();

    // Merge with position.completedAreas for validation
    const allCompleted = new Set([
      ...(resumeFromPosition?.completedAreas || []),
      ...actuallyCompleted
    ]);

    console.log('[RiskAutopilotV2] 🔄 Resume initialization:', {
      resumeFromPosition,
      actuallyCompleted,
      allCompleted: Array.from(allCompleted)
    });

    // Initialize statusByArea
    setStatusByArea((prev) => {
      const next = { ...prev };
      areas.forEach((area, index) => {
        if (!next[area]) {
          const isCompleted = allCompleted.has(area);
          next[area] = {
            status: isCompleted ? 'completed' :
                    index === (resumeFromPosition?.currentAreaIndex ?? 0) ? 'queued' :
                    index < (resumeFromPosition?.currentAreaIndex ?? 0) ? 'completed' : 'waiting',
            currentTask: null,
            completedTasks: isCompleted ? [...RISK_TASKS.map(t => t.key)] : [],
            startedAt: null,
            completedAt: isCompleted ? new Date().toISOString() : null
          };
        }
      });
      return next;
    });

    // Hydrate assessmentProgress slice from resume state
    areas.forEach((area, index) => {
      const isCompleted = allCompleted.has(area) || index < (resumeFromPosition?.currentAreaIndex ?? -1);
      const status = isCompleted ? 'COMPLETE' : 'IN_PROGRESS';
      const progress = isCompleted ? 100 : 0;
      dispatch(setAreaStatus({ assessmentUuid, areaName: area, status }));
      dispatch(updateAreaProgress({ assessmentUuid, areaName: area, progress }));
    });

    // Set starting index to resume area
    if (resumeFromPosition?.currentAreaIndex >= 0) {
      const resumeIndex = resumeFromPosition.currentAreaIndex;
      const resumeArea = areas[resumeIndex];

      // Mark checkpoint as resumed
      dispatch(markResumed({ assessmentUuid }));

      // If resume area is already complete, find next incomplete
      if (allCompleted.has(resumeArea)) {
        const nextIncomplete = areas.findIndex(a => !allCompleted.has(a));
        const targetIndex = nextIncomplete >= 0 ? nextIncomplete : areas.length;
        console.log(`[RiskAutopilotV2] Resume area "${resumeArea}" already complete, jumping to index ${targetIndex}`);
        setCurrentAreaIndex(targetIndex);
      } else {
        console.log(`[RiskAutopilotV2] Resuming at area "${resumeArea}" (index ${resumeIndex})`);
        setCurrentAreaIndex(resumeIndex);
      }
    } else {
      // Fresh start - begin at 0 (handles both no resumeFromPosition and missing currentAreaIndex)
      console.log('[RiskAutopilotV2] Starting from first area (index 0)');
      setCurrentAreaIndex(0);
    }
  }, [areas, resumeFromPosition, dispatch, assessmentUuid]);


  // Use approved narrative from context step instead of generating new one
  const riskNarrative = approvedNarrative || '';

  // NOTE: Now using approved narrative from context step instead of generating new one

  /**
   * Save progress checkpoint to database and Redux
   * Called when area starts, changes phase, or completes
   */
  const saveProgressCheckpoint = useCallback(async (areaName, areaIndex, phase = null) => {
    try {
      if (hasMissingUploadData) {
        setError('Upload data missing. Please re-upload your model card before continuing.');
        return;
      }

      const completedAreas = areas.slice(0, areaIndex).filter(a =>
        statusByArea[a]?.status === 'completed'
      );

      const checkpoint = {
        majorStep: 'risks',
        currentArea: areaName,
        currentAreaIndex: areaIndex,
        completedAreas,
        phase,
        narrativesApproved: true,
        uploaded: { bucket, path },
        pausedAt: new Date().toISOString()
      };

      // Save to assessmentProgressSlice (new RTK-first approach)
      dispatch(saveCheckpoint({ assessmentUuid, checkpoint }));

      // Save checkpoint server-side without overwriting main position
      await dispatch(setAssessmentCheckpoint({ modelId, assessmentUuid, checkpoint })).unwrap();

      // Update assessment progress slice for area status
      const status = phase === 'completed' ? 'COMPLETE' : 'IN_PROGRESS';
      const progress = progressForPhase(phase);
      dispatch(setAreaStatus({ assessmentUuid, areaName, status }));
      dispatch(updateAreaProgress({ assessmentUuid, areaName, progress }));

      console.log(`[RiskAutopilotV2] ✅ Checkpoint saved: ${areaName} (${phase || 'start'})`);
    } catch (error) {
      console.error('[RiskAutopilotV2] Failed to save checkpoint:', error);
      setError('Could not persist your progress. Please check your connection and try again.');
    }
  }, [dispatch, modelId, assessmentUuid, areas, statusByArea, bucket, path, hasMissingUploadData]);

  // Agentic mode: single orchestrated call instead of sequential area processing
  const [agenticRunning, setAgenticRunning] = useState(false);
  const agenticTriggeredRef = useRef(false);

  useEffect(() => {
    if (assessmentMode !== 'agentic') return;
    if (agenticTriggeredRef.current || agenticRunning) return;
    if (!areas.length || hasMissingUploadData) return;

    agenticTriggeredRef.current = true;
    setAgenticRunning(true);

    const runAgentic = async () => {
      try {
        // Mark all areas as generating
        const newStatus = {};
        areas.forEach(a => { newStatus[a] = { status: 'generating', tasks: {} }; });
        setStatusByArea(newStatus);

        const response = await authFetch('/.netlify/functions/agentic-assess', {
          method: 'POST',
          body: JSON.stringify({
            assessmentUuid,
            modelId,
            assessmentAreas: areas.filter(a => a !== 'Security Assessment').map(a => ({
              name: a,
              definition: riskFormData[a]?.areaDefinition || '',
            })),
            contextData: contextData || {},
            bucket,
            path,
          }),
        });

        if (!response.ok) {
          throw new Error(`Agentic assessment failed: ${response.status}`);
        }

        const result = await response.json();
        console.log('[RiskAutopilot] Agentic assessment result:', result);

        // Mark all areas as completed
        const completedStatus = {};
        areas.forEach(a => { completedStatus[a] = { status: 'completed', tasks: {} }; });
        setStatusByArea(completedStatus);

        // Refresh assessment data
        dispatch(fetchRiskCardData({ modelId, assessmentUuid }));

        if (typeof onComplete === 'function') {
          onComplete();
        }
      } catch (err) {
        console.error('[RiskAutopilot] Agentic mode error:', err);
        setError(`Agentic assessment failed: ${err.message}`);
        // Reset areas to queued on failure
        const resetStatus = {};
        areas.forEach(a => { resetStatus[a] = { status: 'queued', tasks: {} }; });
        setStatusByArea(resetStatus);
      } finally {
        setAgenticRunning(false);
      }
    };

    runAgentic();
  }, [assessmentMode, areas, hasMissingUploadData, agenticRunning, assessmentUuid, modelId, riskFormData, contextData, bucket, path, dispatch, onComplete]);

  // Enhanced runner with sequential area processing (prompt mode only)
  useEffect(() => {
    if (assessmentMode === 'agentic') return; // Skip in agentic mode

    const anyGenerating = Object.values(statusByArea).some((s) => s.status === 'generating');
    if (anyGenerating || waitingForDecision) return;
    
    const currentArea = areas[currentAreaIndex];
    if (!currentArea) {
      // All areas processed - clear checkpoint and call completion handler
      dispatch(clearCheckpoint({ assessmentUuid }));
      if (typeof onComplete === 'function') {
        onComplete();
      }
      return;
    }
    
    // Report current area progress to parent (areas already includes security as 7th item)
    if (typeof onAreaProgress === 'function') {
      const completedCount = areas.filter((a, idx) => {
        if (a === 'Security Assessment') {
          return statusByArea.security?.status === 'completed';
        }
        return statusByArea[a]?.status === 'completed';
      }).length;

      onAreaProgress({
        currentArea: currentArea,
        currentIndex: currentAreaIndex + 1,
        total: areas.length, // Already includes security as 7th area
        completed: completedCount
      });
    }
    
    // Handle Security Assessment separately (it has its own flow)
    if (currentArea === 'Security Assessment') {
      const areaStatus = statusByArea[currentArea]?.status;
      if (areaStatus === 'queued') {
        console.log('[RiskAutopilot] 🛡️ Starting Security Assessment...');
        runSecurityAssessment();
      }
    } else {
      // Regular risk areas
      const areaStatus = statusByArea[currentArea]?.status;
      if (areaStatus === 'queued') {
        runAreaWithServices(currentArea);
      }
    }
    // Note: area completion decision handling is now manual via user actions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusByArea, areas, currentAreaIndex, waitingForDecision]);

  async function runAreaWithServices(areaName) {
    try {
      if (hasMissingUploadData) {
        setError('Upload data missing. Please re-upload your model card before continuing.');
        return;
      }
      setError(null);
      
      // Start the area
      setStatusByArea((prev) => ({ 
        ...prev, 
        [areaName]: { 
          ...prev[areaName],
          status: 'generating',
          currentTask: 'researching',
          startedAt: new Date().toISOString()
        }
      }));

      // Get area definition from TAXONOMY (template structure), not riskFormData (saved data)
      // riskFormData contains saved risk data, while riskFormTaxonomy has the structure from template
      const areaDefinition = riskFormTaxonomy[areaName];

      if (!areaDefinition) {
        console.error(`[RiskAutopilot] ❌ No area definition found for "${areaName}" in taxonomy:`, Object.keys(riskFormTaxonomy));
        throw new Error(`Area definition not found for: ${areaName}`);
      }

      console.log(`[RiskAutopilot] Starting ${areaName} with full service execution...`, { areaDefinition: !!areaDefinition });

      // Save checkpoint: Starting area
      await saveProgressCheckpoint(areaName, currentAreaIndex, 'discovery');

      // Phase 1: Risk Discovery (Tasks 1-4: Analyzing Area, Researching Sources, Researching Causes, Generating Scenarios)
      let lastTask = null;
      const discoveryResults = await discoveryService.executeDiscovery(
        areaName, 
        areaDefinition, 
        { riskNarrative }, // V2: This now contains use case analysis instead of structured narrative
        riskCauseEvalCfg,
        (currentTask) => {
          console.log(`[Discovery] Task progress: ${lastTask} -> ${currentTask}`);
          updateTaskProgress(areaName, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[Discovery] Results:`, discoveryResults);
      // Note: Discovery results are now updated live via handleLiveUpdate callback

      // Save checkpoint: Discovery complete
      await saveProgressCheckpoint(areaName, currentAreaIndex, 'evaluation');

      // Phase 2: Risk Evaluation (Task 6: Setting priorities)
      // Pass riskFormTaxonomy (template structure with causes) alongside riskFormData (saved data)
      const evaluationResults = await evaluationService.executeEvaluation(
        areaName,
        riskFormData,
        assessmentMeta,
        riskFormTaxonomy,  // NEW: Pass taxonomy for cause extraction
        (currentTask) => {
          console.log(`[Evaluation] Task progress: ${lastTask} -> ${currentTask}`);
          updateTaskProgress(areaName, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[Evaluation] Results:`, evaluationResults);

      // Update outputs with evaluation results
      setAreaOutputs((prev) => ({
        ...prev,
        [areaName]: {
          ...prev[areaName],
          evaluations: evaluationResults
        }
      }));

      // Save checkpoint: Evaluation complete
      await saveProgressCheckpoint(areaName, currentAreaIndex, 'actions');

      // Phase 3: Action Planning (Task 7: Planning actions)
      const actionResults = await actionService.executeActionPlanning(
        areaName,
        riskFormData,
        assessmentMeta,
        contextData,
        (currentTask) => {
          console.log(`[Actions] Task progress: ${lastTask} -> ${currentTask}`);
          updateTaskProgress(areaName, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[Actions] Results:`, actionResults);

      // Update outputs with action results
      setAreaOutputs((prev) => ({
        ...prev,
        [areaName]: {
          ...prev[areaName],
          actions: actionResults
        }
      }));

      // Save checkpoint: Actions complete
      await saveProgressCheckpoint(areaName, currentAreaIndex, 'residual');

      // Phase 4: Residual Risk (Task 8: Calculating residual risk)
      const residualResults = await residualService.executeResidualAssessment(
        areaName,
        riskFormData,
        assessmentMeta,
        controlState,
        (currentTask) => {
          console.log(`[Residual] Task progress: ${lastTask} -> ${currentTask}`);
          updateTaskProgress(areaName, lastTask, currentTask);
          lastTask = currentTask;
        }
      );
      console.log(`[Residual] Results:`, residualResults);

      // Update outputs with residual results
      setAreaOutputs((prev) => ({ 
        ...prev, 
        [areaName]: { 
          ...prev[areaName],
          residual: residualResults 
        }
      }));

      // Mark area as completed with final task progression
      updateTaskProgress(areaName, 'residual', null, true);

      // Save checkpoint: Area complete
      await saveProgressCheckpoint(areaName, currentAreaIndex, 'completed');

      // Skip problematic area status save that's causing database errors
      // dispatch(setAreaStatus(modelId, areaName, 'COMPLETED', assessmentUuid));

      // Refresh Risk Cards from DB via RTK async thunk
      dispatch(fetchRiskCardData({ assessmentUuid, modelId }));

      // Show brief completion notification, then auto-continue
      const finalResults = {
        ...discoveryResults,
        evaluations: evaluationResults,
        actions: actionResults,
        residual: residualResults
      };
      setCompletedAreaData({
        areaName,
        results: finalResults,
        isLastArea: currentAreaIndex === areas.length - 1
      });

      const currentAutonomous = autonomousRef.current;
      console.log(`[RiskAutopilot] 🔄 Area completion logic - autonomous: ${autonomous} (prop), ${currentAutonomous} (ref)`);
      console.log(`[RiskAutopilot] 🔄 Current area: ${areaName}, index: ${currentAreaIndex}, total areas: ${areas.length}`);
      console.log(`[RiskAutopilot] 🔄 Autonomous prop type: ${typeof autonomous}, value: ${JSON.stringify(autonomous)}`);
      
      if (currentAutonomous) {
        // Autonomous mode: auto-continue to next area
        console.log(`[RiskAutopilot] 🤖 AUTONOMOUS MODE: auto-continuing to next area`);
        setWaitingForDecision(false);
        handleContinueToNext();
      } else {
        // Supervised mode: user must choose to continue (unchanged)
        console.log(`[RiskAutopilot] 👤 SUPERVISED MODE: waiting for user decision`);
        setWaitingForDecision(true);
      }

    } catch (e) {
      setError(e.message);
      setStatusByArea((prev) => ({ 
        ...prev, 
        [areaName]: { 
          ...prev[areaName],
          status: 'failed' 
        }
      }));
    }
  }

  function updateTaskProgress(areaName, currentTask, nextTask, completed = false) {
    setStatusByArea((prev) => {
      const areaStatus = prev[areaName] || {};
      const completedTasks = [...(areaStatus.completedTasks || [])];
      
      if (currentTask && !completedTasks.includes(currentTask)) {
        completedTasks.push(currentTask);
      }

      return {
        ...prev,
        [areaName]: {
          ...areaStatus,
          currentTask: completed ? null : nextTask,
          completedTasks,
          status: completed ? 'completed' : 'generating',
          completedAt: completed ? new Date().toISOString() : areaStatus.completedAt
        }
      };
    });
  }

  // Run security assessment after all 6 risk areas complete
  const runSecurityAssessment = useCallback(async () => {
    try {
      console.log('[RiskAutopilot] 🛡️ Starting security threat assessment');

      // Initialize security status under both keys for compatibility
      setStatusByArea(prev => ({
        ...prev,
        'Security Assessment': {
          status: 'generating',
          currentTask: 'generating scenarios',
          completedTasks: [],
          startedAt: new Date().toISOString()
        },
        security: {
          status: 'generating',
          currentTask: 'generating scenarios',
          completedTasks: [],
          startedAt: new Date().toISOString()
        }
      }));

      // Save checkpoint: Starting security assessment
      await saveProgressCheckpoint('Security Assessment', currentAreaIndex, 'security');

      const modelType = assessmentMeta?.model_type || assessmentMeta?.modelType || 'predictive';

      // Execute security service
      const results = await securityService.executeSecurityAssessment(
        contextData,
        riskFormData,
        modelType,
        (currentTask) => {
          console.log('[RiskAutopilot] 🛡️ Security task:', currentTask);
          setStatusByArea(prev => ({
            ...prev,
            'Security Assessment': {
              ...prev['Security Assessment'],
              currentTask
            },
            security: {
              ...prev.security,
              currentTask
            }
          }));
        }
      );

      console.log('[RiskAutopilot] 🛡️ Security assessment complete:', results);

      // Mark security as completed under both keys
      setStatusByArea(prev => ({
        ...prev,
        'Security Assessment': {
          ...prev['Security Assessment'],
          status: 'completed',
          currentTask: null,
          completedAt: new Date().toISOString(),
          results
        },
        security: {
          ...prev.security,
          status: 'completed',
          currentTask: null,
          completedAt: new Date().toISOString(),
          results
        }
      }));

      // Save checkpoint: Security complete
      await saveProgressCheckpoint('Security Assessment', currentAreaIndex, 'completed');

      // Refresh risk cards from DB via RTK async thunk
      dispatch(fetchRiskCardData({ assessmentUuid, modelId }));

      // Complete assessment
      console.log('[RiskAutopilot] ✅ All phases complete (6 areas + security), triggering completion');

      if (typeof onComplete === 'function') {
        onComplete();
      }

    } catch (error) {
      console.error('[RiskAutopilot] ❌ Security assessment failed:', error);
      setError(`Security assessment failed: ${error.message}`);

      setStatusByArea(prev => ({
        ...prev,
        'Security Assessment': {
          ...prev['Security Assessment'],
          status: 'failed',
          error: error.message
        },
        security: {
          ...prev.security,
          status: 'failed',
          error: error.message
        }
      }));

      // Don't auto-complete on failure - let user decide to retry or skip
      console.log('[RiskAutopilot] ⚠️ Security assessment failed - waiting for user decision');
    }
  }, [securityService, contextData, riskFormData, assessmentMeta, dispatch, modelId, assessmentUuid, currentAreaIndex, saveProgressCheckpoint, onComplete]);

  // Decision handlers
  const handleContinueToNext = useCallback(() => {
    console.log(`[RiskAutopilot] 🚀 handleContinueToNext called`);
    console.log(`[RiskAutopilot] 🚀 Current state - currentAreaIndex: ${currentAreaIndex}, areas.length: ${areas.length}`);
    console.log(`[RiskAutopilot] 🚀 Areas:`, areas);

    console.log(`[RiskAutopilot] 🚀 Setting waitingForDecision = false, completedAreaData = null`);
    setWaitingForDecision(false);
    setCompletedAreaData(null);

    // Move to next area if available
    if (currentAreaIndex + 1 < areas.length) {
      const nextAreaIndex = currentAreaIndex + 1;
      const nextArea = areas[nextAreaIndex];
      console.log(`[RiskAutopilot] 🚀 Moving to next area: ${nextArea} (index ${nextAreaIndex})`);

      setCurrentAreaIndex(prev => {
        console.log(`[RiskAutopilot] 🚀 setCurrentAreaIndex: ${prev} -> ${prev + 1}`);
        return prev + 1;
      });

      setStatusByArea(prev => {
        const newStatus = {
          ...prev,
          [nextArea]: { ...prev[nextArea], status: 'queued' }
        };
        console.log(`[RiskAutopilot] 🚀 Updated area status:`, newStatus);
        return newStatus;
      });
    } else {
      console.log(`[RiskAutopilot] 🚀 All 6 risk areas complete, starting security assessment`);
      // All risk areas complete - start security phase
      runSecurityAssessment();
    }
  }, [setWaitingForDecision, setCompletedAreaData, currentAreaIndex, areas, setCurrentAreaIndex, setStatusByArea, runSecurityAssessment]);

  const handleSkipArea = useCallback((areaToSkip) => {
    // Mark area as skipped
    setStatusByArea(prev => ({
      ...prev,
      [areaToSkip]: { ...prev[areaToSkip], status: 'skipped' }
    }));
    
    handleContinueToNext();
  }, [setStatusByArea, handleContinueToNext]);

  // Event listener for skip area functionality
  useEffect(() => {
    const handleSkipAreaEvent = (event) => {
      const { areaName } = event.detail;
      if (areaName && areas.includes(areaName)) {
        handleSkipArea(areaName);
      }
    };

    window.addEventListener('skipFailedArea', handleSkipAreaEvent);
    return () => {
      window.removeEventListener('skipFailedArea', handleSkipAreaEvent);
    };
  }, [areas, handleSkipArea]);

  const handleGenerateReport = () => {
    // Complete the assessment with current results
    setWaitingForDecision(false);
    setCompletedAreaData(null);
    
    if (typeof onComplete === 'function') {
      onComplete();
    }
  };

  const getTaskProgress = (areaName) => {
    const areaStatus = statusByArea[areaName] || {};
    const activeTasks = RISK_TASKS.filter(task => !task.placeholder);
    const completedCount = areaStatus.completedTasks?.length || 0;
    return {
      completed: completedCount,
      total: activeTasks.length,
      percentage: Math.round((completedCount / activeTasks.length) * 100)
    };
  };


  const currentArea = areas[currentAreaIndex];
  const isSecurityArea = currentArea === 'Security Assessment';

  return (
    <Stack gap="md">
      {error && (
        <Alert color="red" icon={<IconAlertTriangle size="1rem" />}>
          {error}
        </Alert>
      )}

      {/* Main Assessment View - All Areas including Security as 7th */}
      <Transition mounted={!!currentArea} transition="fade" duration={400} timingFunction="ease-out">
        {(styles) => currentArea && (
          <div style={styles}>
            {isSecurityArea ? (
              <SecurityAssessmentCard
                status={statusByArea.security || {}}
                outputs={areaOutputs.security}
                autonomous={autonomous}
                totalAreas={areas.length}
                areaIndex={currentAreaIndex + 1}
                onRetry={() => setStatusByArea(prev => ({
                  ...prev,
                  'Security Assessment': { ...prev['Security Assessment'], status: 'queued' },
                  security: { ...prev.security, status: 'queued' }
                }))}
                onGenerateReport={handleGenerateReport}
              />
            ) : (
              <RiskAreaCard
                areaName={currentArea}
                areaIndex={currentAreaIndex + 1}
                totalAreas={areas.length}
                status={statusByArea[currentArea] || {}}
                outputs={areaOutputs[currentArea]}
                progress={getTaskProgress(currentArea)}
                autonomous={autonomous}
                onRetry={() => setStatusByArea(prev => ({
                  ...prev,
                  [currentArea]: { ...prev[currentArea], status: 'queued' }
                }))}
                // Pass completion state for graceful user control
                isWaitingForUserDecision={waitingForDecision}
                completedAreaData={completedAreaData}
                remainingAreas={areas.slice(currentAreaIndex + 1)}
                onContinue={handleContinueToNext}
                onSkip={handleSkipArea}
                onGenerateReport={handleGenerateReport}
              />
            )}
          </div>
        )}
      </Transition>
    </Stack>
  );
}

/**
 * Individual Risk Area Card with beautiful task progression
 */
function RiskAreaCard({ 
  areaName, 
  areaIndex,
  totalAreas,
  status, 
  outputs, 
  progress, 
  autonomous,
  onRetry,
  // New props for graceful continuation
  isWaitingForUserDecision,
  completedAreaData,
  remainingAreas,
  onContinue,
  onSkip,
  onGenerateReport
}) {
  const isGenerating = status.status === 'generating';
  const isCompleted = status.status === 'completed';
  const isFailed = status.status === 'failed';
  const isQueued = status.status === 'queued';

  // Area icon mapping based on actual risk areas from database
  const getAreaIcon = (areaName) => {
    const AREA_ICONS = {
      'Misinformation': IconAlertTriangle,
      'Malicious actors': IconTarget, 
      'Discrimination & toxicity': IconBrain,
      'Human-computer interaction': IconUsers,
      'Socioeconomic & environmental': IconZoomScan,
      'AI system safety, failures & limitations': IconShieldCheck
    };
    
    return AREA_ICONS[areaName] || IconAnalyze; // Default icon for unknown areas
  };

  const AreaIcon = getAreaIcon(areaName);

  // Helper to get task-specific results with actual service data structures
  const getTaskResults = (taskKey, outputs) => {
    if (!outputs) return null;
    
    console.log(`[getTaskResults] Processing task: ${taskKey}`, outputs);
    
    switch (taskKey) {
      case 'analyzing risk area':
        // Show area analysis results
        if (outputs.areaCard && outputs.areaCard.summary) {
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="indigo">📄 Area Analysis Complete</Text>
              {outputs.areaCard.summary.slice(0, 3).map((bullet, idx) => (
                <Text key={idx} size="xs" c="dimmed" lineClamp={2}>
                  • {bullet}
                </Text>
              ))}
            </Stack>
          );
        }
        return null;

      case 'researching risk sources':
        // Show source research results when available
        if (outputs.selectedSources && Array.isArray(outputs.selectedSources) && outputs.selectedSources.length > 0) {
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="blue">🔍 Source Research Complete ({outputs.selectedSources.length} sources)</Text>
              {outputs.selectedSources.slice(0, 2).map((source, idx) => (
                <Box key={idx} p="xs" bg="blue.0" radius="sm" mb="xs">
                  <Text size="xs" fw={500} c="dark.7" lineClamp={1}>
                    {source.sourceName || `Source ${idx + 1}`}
                  </Text>
                  <Text size="xs" c="blue.6">
                    🤖 {source.aiConfidence >= 0.8 ? 'High' : source.aiConfidence >= 0.5 ? 'Medium' : 'Low'} Confidence ({(source.aiConfidence || 0).toFixed(1)})
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    📄 {source.evidenceSnippets?.[0] || 'Evidence analyzed'}
                  </Text>
                  <Text size="xs" c="blue.5" lineClamp={2}>
                    💡 {source.rationale || 'Assessment completed'}
                  </Text>
                </Box>
              ))}
              {outputs.selectedSources.length > 2 && (
                <Text size="xs" c="dimmed">+{outputs.selectedSources.length - 2} more sources analyzed</Text>
              )}
            </Stack>
          );
        }
        return null;

      case 'researching root causes':
        // Show cause research results
        if (outputs.causesBySource && Object.keys(outputs.causesBySource).length > 0) {
          const sourcesWithCauses = Object.keys(outputs.causesBySource);
          let totalCauses = 0;
          sourcesWithCauses.forEach(sourceName => {
            const causes = outputs.causesBySource[sourceName] || [];
            causes.forEach(cat => {
              totalCauses += (cat.researchedCauses?.length || 0);
            });
          });
          
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="orange">⚙️ Cause Research Complete ({totalCauses} causes)</Text>
              {sourcesWithCauses.slice(0, 2).map((sourceName, idx) => {
                const causes = outputs.causesBySource[sourceName] || [];
                const sourceTotal = causes.reduce((sum, cat) => sum + (cat.researchedCauses?.length || 0), 0);
                return (
                  <Text key={idx} size="xs" c="dimmed">
                    🔍 {sourceName}: {sourceTotal} causes researched
                  </Text>
                );
              })}
              {sourcesWithCauses.length > 2 && (
                <Text size="xs" c="dimmed">+{sourcesWithCauses.length - 2} more sources researched</Text>
              )}
            </Stack>
          );
        }
        return null;

      case 'preparing causes for RAG':
        // Show cause preparation for RAG results
        if (outputs.relevantCauses && outputs.relevantCauses.length > 0) {
          const sourceCount = [...new Set(outputs.relevantCauses.map(c => c.source))].length;
          const categoryCount = outputs.totalCauseCategories || 0;
          
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="violet">🎯 RAG Preparation Complete ({outputs.relevantCauses.length} causes)</Text>
              <Text size="xs" c="dimmed">
                ✅ {outputs.relevantCauses.length} high-confidence causes from {sourceCount} sources
              </Text>
              {categoryCount > 0 && (
                <Text size="xs" c="dimmed">
                  📊 Analyzed {categoryCount} cause categories for context enrichment
                </Text>
              )}
            </Stack>
          );
        }
        return null;

      case 'researching':
        // Fallback for old researching task name
        return getTaskResults('researching risk sources', outputs);
        
      case 'assigning causes to sources':
        // Show assignment and evaluation results
        if (outputs.finalAssignments && outputs.finalAssignments.length > 0) {
          const totalSelected = outputs.finalAssignments.reduce((sum, assignment) => sum + (assignment.selectedOptions?.length || 0), 0);
          const totalSources = outputs.finalAssignments.length;
          
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="green">⚖️ Assignments Complete</Text>
              <Text size="xs" c="dimmed">
                ✅ {totalSelected} causes assigned to {totalSources} sources
              </Text>
              {outputs.evaluations?.length > 0 && (
                <Text size="xs" c="dimmed">
                  📊 {outputs.evaluations.length} evaluations completed
                </Text>
              )}
              {outputs.finalAssignments.slice(0, 2).map((assignment, idx) => (
                <Text key={idx} size="xs" c="dimmed">
                  ⚙️ {assignment.sourceName}: {assignment.selectedOptions?.length || 0} causes
                </Text>
              ))}
              {outputs.finalAssignments.length > 2 && (
                <Text size="xs" c="dimmed">+{outputs.finalAssignments.length - 2} more sources</Text>
              )}
            </Stack>
          );
        }
        // Fallback for assignment in progress
        if (outputs.selectedSources?.length > 0) {
          return (
            <Text size="xs" c="green">⚖️ Assigning causes to {outputs.selectedSources.length} sources...</Text>
          );
        }
        return null;

      case 'assigning':
        // Fallback for old assigning task name
        return getTaskResults('assigning causes to sources', outputs);
        
      case 'evaluating':
        // Show cause evaluation results
        if (outputs.evaluations?.length > 0) {
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="orange">⚡ Cause Evaluations ({outputs.evaluations.length})</Text>
              {outputs.evaluations.slice(0, 2).map((evaluation, idx) => (
                <Text key={idx} size="xs" c="dimmed">
                  📊 {evaluation.sourceName}: {evaluation.category || 'Evaluated'}
                </Text>
              ))}
              {outputs.evaluations.length > 2 && (
                <Text size="xs" c="dimmed">+{outputs.evaluations.length - 2} more evaluations...</Text>
              )}
            </Stack>
          );
        } else if (outputs.selectedSources?.length > 0) {
          return (
            <Text size="xs" c="orange">⚡ Cause evaluation completed for {outputs.selectedSources.length} sources</Text>
          );
        }
        break;
        
      case 'scenarios':
        // Show RAG scenario generation progress with metadata
        if (outputs.scenarios?.length > 0 || outputs.scenarioStats) {
          const stats = outputs.scenarioStats || {};
          return (
            <Stack gap="xs">
              <Group spacing="xs">
                <Text size="xs" fw={500} c="purple">🎭 RAG Scenarios ({stats.totalGenerated || 0})</Text>
                {stats.generationMethod && (
                  <Badge size="xs" variant="light" color="violet">{stats.generationMethod}</Badge>
                )}
              </Group>
              
              {stats.cataloguesUsed?.length > 0 && (
                <Group spacing="xs">
                  <Text size="xs" c="dimmed">📚 Risk Catalogues:</Text>
                  {stats.cataloguesUsed.slice(0, 3).map((cat, idx) => (
                    <Badge key={idx} size="xs" variant="outline" color="blue">
                      {cat}
                    </Badge>
                  ))}
                  {stats.cataloguesUsed.length > 3 && (
                    <Text size="xs" c="dimmed">+{stats.cataloguesUsed.length - 3} more</Text>
                  )}
                </Group>
              )}
              
              <Text size="xs" c="green">
                ✅ {stats.totalPromoted || 0} scenarios promoted to risks
              </Text>
              
              {outputs.scenarios?.slice(0, 2).map((scenario, idx) => (
                <Box key={idx}>
                  <Text size="xs" fw={500} lineClamp={2}>
                    {scenario.scenarioText || scenario.description || scenario.scenario || 'Scenario generated'}
                  </Text>
                  <Text size="xs" c="dimmed">From: {stats.cataloguesUsed?.[0] || 'AI Generated'}</Text>
                </Box>
              ))}
              {(outputs.scenarios?.length || 0) > 2 && (
                <Text size="xs" c="dimmed">+{outputs.scenarios.length - 2} more scenarios...</Text>
              )}
            </Stack>
          );
        } else if (outputs.selectedSources?.length > 0) {
          const analyzedCount = outputs.selectedSources.length;
          return (
            <Text size="xs" c="purple">🎭 RAG: Generating scenarios from {analyzedCount} sources + risk catalogues...</Text>
          );
        }
        break;
        
        
      case 'evaluation':
        // Risk priority evaluation results - LIVE STREAMING
        if (outputs.riskEvaluations?.length > 0) {
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="green">⭐ Risk Priority Evaluation</Text>
              <Text size="xs" c="dimmed">
                📊 Progress: {outputs.evaluationProgress || `${outputs.riskEvaluations.length} completed`}
              </Text>
              {outputs.riskEvaluations.slice(-2).map((evaluation, idx) => (
                <Box key={idx} p="xs" bg="green.1" style={{ borderRadius: 4 }}>
                  <Text size="xs" fw={500}>{evaluation.riskName}</Text>
                  <Text size="xs" c="dimmed">Priority: {evaluation.priority}</Text>
                </Box>
              ))}
              {outputs.riskEvaluations.length > 2 && (
                <Text size="xs" c="dimmed">+{outputs.riskEvaluations.length - 2} more risks evaluated...</Text>
              )}
            </Stack>
          );
        } else if (outputs.evaluations?.evaluatedRisks > 0) {
          // Fallback to original format if live data not available
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="green">⭐ Risk Priority Evaluation</Text>
              <Text size="xs" c="dimmed">
                ✅ {outputs.evaluations.evaluatedRisks} risks prioritized with AI analysis
              </Text>
            </Stack>
          );
        } else {
          return (
            <Group gap="xs" align="center">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={12} />
              <Text size="xs" c="blue">Huginn is evaluating risk priorities and analyzing scenarios...</Text>
            </Group>
          );
        }
        
      case 'actions':
        // Action planning results - LIVE STREAMING
        if (outputs.controlUpdates?.length > 0 || outputs.controlEvaluations?.length > 0) {
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="orange">🎯 Action Planning</Text>
              {outputs.controlUpdates?.slice(-2).map((update, idx) => (
                <Box key={idx} p="xs" bg="orange.1" style={{ borderRadius: 4 }}>
                  <Text size="xs" fw={500} mb={4}>{update.riskName}</Text>
                  <Text size="xs" c="dimmed" mb={6}>🛠️ {update.controlsCount} controls generated</Text>

                  {/* Display individual controls with policy evidence badges */}
                  {update.controls && update.controls.length > 0 && (
                    <Stack gap={4}>
                      {update.controls.map((control, cIdx) => (
                        <Box key={control.controlId || cIdx} p={6} bg="white" style={{ borderRadius: 4, border: '1px solid #e9ecef' }}>
                          <Text size="xs" fw={500} lineClamp={2}>{control.name}</Text>
                          {control.description && (
                            <Text size="xs" c="dimmed" lineClamp={2} mt={2}>{control.description}</Text>
                          )}
                          {/* Show policy evidence badges inline */}
                          {control.policyEvidence && control.policyEvidence.length > 0 && (
                            <div style={{ marginTop: 4 }}>
                              <PolicyEvidenceBadges
                                policyEvidence={control.policyEvidence}
                                compact={true}
                                expandable={false}
                              />
                            </div>
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              ))}
              {outputs.controlEvaluations?.slice(-1).map((evaluation, idx) => (
                <Box key={idx} p="xs" bg="orange.1" style={{ borderRadius: 4 }}>
                  <Text size="xs" fw={500}>{evaluation.controlName}</Text>
                  <Text size="xs" c="dimmed">✅ Evaluated for {evaluation.riskName}</Text>
                </Box>
              ))}
              {(outputs.controlUpdates?.length > 2 || outputs.controlEvaluations?.length > 1) && (
                <Text size="xs" c="dimmed">More controls being generated and evaluated...</Text>
              )}
            </Stack>
          );
        } else if (outputs.actions?.actionsGenerated > 0) {
          // Fallback to original format
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="orange">🎯 Action Planning Complete</Text>
              <Text size="xs" c="dimmed">
                🛠️ {outputs.actions.actionsGenerated} controls generated and evaluated
              </Text>
              <Text size="xs" c="dimmed">
                📊 {outputs.actions.evaluatedRisks} risks covered with mitigation plans
              </Text>
            </Stack>
          );
        } else {
          return (
            <Group gap="xs" align="center">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={12} />
              <Text size="xs" c="blue">Huginn is developing mitigation strategies and control recommendations...</Text>
            </Group>
          );
        }
        
      case 'residual':
        // Residual risk assessment results - LIVE STREAMING with rating badges
        // Use consistent color palette from RiskLandscape (riskPriorityColors)
        const riskPriorityColors = {
          Critical: '#1A1A1A',    // Black
          High: '#993333',        // Red
          Significant: '#B94D4D', // Amber
          Moderate: '#D4AA7D',    // Light Orange
          Low: '#B8C2B7',         // Muted Green
          Negligible: '#D8D8D8',  // Light Grey
          None: '#EBEBEB',        // Very Light Grey
        };

        if (outputs.residualCalculations?.length > 0) {
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="teal">🛡️ Residual Risk Analysis</Text>
              <Text size="xs" c="dimmed">
                📊 Progress: {outputs.residualProgress || `${outputs.residualCalculations.length} completed`}
              </Text>
              {outputs.residualCalculations.slice(-3).map((calc, idx) => (
                <Box key={idx} p="xs" bg="teal.0" style={{ borderRadius: 6, border: '1px solid var(--mantine-color-teal-2)' }}>
                  <Group justify="space-between" align="center">
                    <Text size="xs" fw={500} style={{ flex: 1 }}>{calc.riskName}</Text>
                    {calc.residualRating && (
                      <Badge
                        size="sm"
                        variant="filled"
                        radius="sm"
                        style={{
                          backgroundColor: riskPriorityColors[calc.residualRating] || '#D8D8D8',
                          color: ['Critical', 'High', 'Significant'].includes(calc.residualRating) ? '#fff' : '#333'
                        }}
                      >
                        {calc.residualRating}
                      </Badge>
                    )}
                  </Group>
                  {calc.actionPlanRating && (
                    <Text size="xs" c="dimmed" mt={2}>Action Plan: {calc.actionPlanRating}</Text>
                  )}
                </Box>
              ))}
            </Stack>
          );
        } else if (outputs.residual?.residualAssessments > 0) {
          // Fallback to original format
          return (
            <Stack gap="xs">
              <Text size="xs" fw={500} c="teal">🛡️ Residual Risk Analysis</Text>
              <Text size="xs" c="dimmed">
                📈 {outputs.residual.residualAssessments} residual risk calculations completed
              </Text>
              <Text size="xs" c="dimmed">
                🔍 {outputs.residual.risksWithActions} risks analyzed for control effectiveness
              </Text>
            </Stack>
          );
        } else {
          return (
            <Group gap="xs" align="center">
              <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={12} />
              <Text size="xs" c="blue">Huginn is analyzing control effectiveness and calculating residual risk levels...</Text>
            </Group>
          );
        }
        
      default:
        return null;
    }
    return null;
  };

  return (
    <Card 
      withBorder 
      p="lg" 
      radius="md"
      style={{ 
        height: '100%',
        transition: 'all 0.2s ease',
        transform: isGenerating ? 'scale(1.02)' : 'scale(1)',
        boxShadow: isGenerating ? '0 8px 25px rgba(25, 118, 210, 0.15)' : undefined
      }}
    >
      <Stack gap="md" h="100%">
        {/* Header with Icon and Counter */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm" style={{ flex: 1 }} align="flex-start">
            <AreaIcon size={24} color="#1976d2" />
            <div style={{ flex: 1 }}>
              <Group gap="xs" align="baseline">
                <Text fw={700} size="lg" lineClamp={2}>
                  {areaName}
                </Text>
                <Text size="sm" c="dimmed" fw={500}>
                  ({areaIndex}/{totalAreas})
                </Text>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                {isQueued && 'Waiting in queue...'}
                {isGenerating && 'Huginn is working...'}
                {isCompleted && 'Analysis complete'}
                {isFailed && 'Analysis failed'}
              </Text>
            </div>
          </Group>
          
          <Group gap="xs">
            {/* Status Badge - Removed AIGeneratingIcon as requested, keep main center progress */}
            <Badge 
              variant="light" 
              color={
                isCompleted ? 'green' : 
                isGenerating ? 'blue' : 
                isFailed ? 'red' : 'gray'
              }
              size="sm"
            >
              {isCompleted && <IconCheck size={14} />}
              {isGenerating && 'Processing'}
              {isFailed && <IconAlertTriangle size={14} />}
              {isQueued && <IconHourglassHigh size={14} />}
            </Badge>
          </Group>
        </Group>

        {/* Enhanced Progress Ring */}
        <Center>
          <RingProgress
            size={120}
            thickness={8}
            sections={[
              { 
                value: progress.percentage, 
                color: isCompleted ? '#40c057' : isGenerating ? '#1976d2' : '#adb5bd' 
              }
            ]}
            label={
              <Center>
                {isCompleted ? (
                  <IconCheck size={32} color="#40c057" />
                ) : isGenerating ? (
                  <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={28} />
                ) : (
                  <IconAnalyze size={28} color="#adb5bd" />
                )}
              </Center>
            }
          />
        </Center>

        {/* Task Timeline with Expandable Details */}
        <Box style={{ flex: 1 }}>
          <Text size="sm" fw={500} mb="xs">
            Progress Tasks
          </Text>
          <Timeline active={progress.completed} bulletSize={24} lineWidth={2}>
            {RISK_TASKS.filter(task => !task.placeholder).map((task, index) => {
              const isActive = status.currentTask === task.key;
              const isTaskCompleted = status.completedTasks?.includes(task.key);
              
              return (
                <Timeline.Item
                  key={task.key}
                  bullet={
                    <Center style={{ width: 24, height: 24 }}>
                      {isTaskCompleted ? (
                        <IconCheck size={16} color="#40c057" />
                      ) : isActive ? (
                        <AIGeneratingIcon isGenerating className="ai-pulse-strong" size={14} color="#ffffff" />
                      ) : (
                        <Box 
                          style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: '#adb5bd' 
                          }} 
                        />
                      )}
                    </Center>
                  }
                  title={
                    <Text 
                      size="xs" 
                      fw={isActive ? 600 : 400}
                      c={isTaskCompleted ? 'green' : isActive ? 'blue' : 'dimmed'}
                    >
                      {task.label}
                    </Text>
                  }
                >
                  <Stack gap="xs" mt={2}>
                    <Text size="xs" c="dimmed">
                      {task.description}
                    </Text>
                    
                    {/* Live Details for Current Task */}
                    {isActive && (
                      <Box p="xs" bg="blue.0" style={{ borderRadius: 4 }}>
                        <Group gap="xs" align="center">
                          <AIGeneratingIcon isGenerating size={12} />
                          <Text size="xs" c="blue">
                            Huginn is working on this task...
                          </Text>
                        </Group>
                      </Box>
                    )}
                    
                    {/* Results for Completed Tasks - Auto-Expanding with Live Updates */}
                    {((isTaskCompleted && outputs && getTaskResults(task.key, outputs)) || 
                      (isActive && outputs && getTaskResults(task.key, outputs))) && (
                      <Collapse in={true} transitionDuration={300}>
                        <Box 
                          p="sm" 
                          bg={isTaskCompleted ? "green.0" : "blue.0"} 
                          style={{ 
                            borderRadius: 6, 
                            border: `1px solid ${isTaskCompleted ? '#c3f53c' : '#74c0fc'}` 
                          }} 
                          mt="xs"
                        >
                          <Group gap="xs" align="flex-start">
                            <Box style={{ 
                              backgroundColor: isTaskCompleted ? '#40c057' : '#1976d2', 
                              width: 4, 
                              minHeight: 20, 
                              borderRadius: 2 
                            }} />
                            <Box style={{ flex: 1 }}>
                              {getTaskResults(task.key, outputs)}
                            </Box>
                          </Group>
                        </Box>
                      </Collapse>
                    )}
                  </Stack>
                </Timeline.Item>
              );
            })}
          </Timeline>
        </Box>

        {/* Quick Results Summary */}
        {isCompleted && outputs && (
          <Box>
            <Divider my="sm" />
            <Text size="sm" fw={600} c="green" mb="xs">
              Analysis Complete
            </Text>
            
            <Group gap="xs" mb="md">
              {outputs.selectedSources?.length > 0 && (
                <Badge size="xs" variant="light" color="blue">
                  {outputs.selectedSources.length} sources analyzed
                </Badge>
              )}
              {outputs.autoPromotedScenarios?.length > 0 && (
                <Badge size="xs" variant="light" color="purple">
                  {outputs.autoPromotedScenarios.length} scenarios
                </Badge>
              )}
              {outputs.evaluations?.evaluatedRisks > 0 && (
                <Badge size="xs" variant="light" color="green">
                  {outputs.evaluations.evaluatedRisks} priorities
                </Badge>
              )}
              {outputs.actions?.actionsGenerated > 0 && (
                <Badge size="xs" variant="light" color="orange">
                  {outputs.actions.actionsGenerated} controls
                </Badge>
              )}
              {outputs.residual?.residualAssessments > 0 && (
                <Badge size="xs" variant="light" color="teal">
                  {outputs.residual.residualAssessments} residual
                </Badge>
              )}
            </Group>
            
            {/* Detailed Summary - Always visible */}
              <Box p="md" bg="gray.0" style={{ borderRadius: 8 }} mt="sm">
                <Stack gap="sm">
                  <Text size="sm" fw={600} mb="xs">📋 Area Summary</Text>
                  
                  {outputs.selectedSources?.length > 0 && (
                    <Box>
                      <Text size="xs" fw={500} c="blue" mb="xs">Risk Sources ({outputs.selectedSources.length})</Text>
                      {outputs.selectedSources.slice(0, 3).map((source, idx) => (
                        <Text key={idx} size="xs" c="dimmed" mb={2}>
                          • {source.sourceName} ✅
                        </Text>
                      ))}
                      {outputs.selectedSources.length > 3 && (
                        <Text size="xs" c="dimmed">+{outputs.selectedSources.length - 3} more sources...</Text>
                      )}
                    </Box>
                  )}
                  
                  {outputs.autoPromotedScenarios?.length > 0 && (
                    <Box>
                      <Text size="xs" fw={500} c="purple" mb="xs">Generated Scenarios ({outputs.autoPromotedScenarios.length})</Text>
                      {outputs.autoPromotedScenarios.slice(0, 2).map((scenario, idx) => (
                        <Box key={idx} mb="xs">
                          <Text size="xs" fw={500}>{scenario.sourceName}</Text>
                          <Text size="xs" c="dimmed" lineClamp={2}>{scenario.scenarioText}</Text>
                        </Box>
                      ))}
                      {outputs.autoPromotedScenarios.length > 2 && (
                        <Text size="xs" c="dimmed">+{outputs.autoPromotedScenarios.length - 2} more scenarios...</Text>
                      )}
                    </Box>
                  )}
                  
                  {outputs.evaluations?.evaluatedRisks > 0 && (
                    <Box>
                      <Text size="xs" fw={500} c="green" mb="xs">Risk Evaluations</Text>
                      <Text size="xs" c="dimmed">✅ {outputs.evaluations.evaluatedRisks} risks prioritized with AI rationale</Text>
                    </Box>
                  )}
                  
                  {outputs.actions?.actionsGenerated > 0 && (
                    <Box>
                      <Text size="xs" fw={500} c="orange" mb="xs">Action Planning</Text>
                      <Text size="xs" c="dimmed">🎯 {outputs.actions.actionsGenerated} controls generated</Text>
                      <Text size="xs" c="dimmed">📊 {outputs.actions.evaluatedRisks} risks with action plans</Text>
                    </Box>
                  )}
                  
                  {outputs.residual?.residualAssessments > 0 && (
                    <Box>
                      <Text size="xs" fw={500} c="teal" mb="xs">Residual Risk</Text>
                      <Text size="xs" c="dimmed">🛡️ {outputs.residual.residualAssessments} residual risks calculated</Text>
                      <Text size="xs" c="dimmed">📈 {outputs.residual.risksWithActions} risks analyzed for effectiveness</Text>
                    </Box>
                  )}
                </Stack>
              </Box>
          </Box>
        )}

        {/* Action Buttons - Always at bottom of area card */}
        <Group justify="center" gap="sm" mt="md">
          {/* While area is generating - show Skip button */}
          {isGenerating && (
            <Button 
              size="sm" 
              variant="light" 
              color="orange"
              onClick={() => {
                // Skip this running area and continue to next
                window.dispatchEvent(new CustomEvent('skipFailedArea', { detail: { areaName } }));
              }}
            >
              Skip Area
            </Button>
          )}
          
          {/* When area is completed - show Continue button or auto-progress message */}
          {isCompleted && (
            <>
              <Text size="sm" fw={500} c="green" mr="md">
                ✅ Analysis Complete!
              </Text>
              
              {autonomous ? (
                // Autonomous mode: show auto-progress message
                <Group gap="xs" align="center">
                  <IconHourglassHigh size={16} color="#1976d2" />
                  <Text size="sm" c="blue">
                    {remainingAreas && remainingAreas.length > 0 
                      ? `Continuing to ${remainingAreas[0]}...`
                      : 'Assessment complete! Proceeding to review...'
                    }
                  </Text>
                </Group>
              ) : (
                // Supervised mode: show manual continue button
                isWaitingForUserDecision && (
                  <>
                    {remainingAreas && remainingAreas.length > 0 ? (
                      <Button 
                        size="md" 
                        color="blue"
                        leftSection={<IconChevronRight size={18} />}
                        onClick={onContinue}
                      >
                        Continue to {remainingAreas[0]}
                      </Button>
                    ) : (
                      <Button 
                        size="md" 
                        color="green"
                        leftSection={<IconCheck size={18} />}
                        onClick={onGenerateReport}
                      >
                        Generate Final Report
                      </Button>
                    )}
                  </>
                )
              )}
            </>
          )}
          
          {/* When area failed - show Retry and Skip */}
          {isFailed && (
            <>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={onRetry} 
                leftSection={<IconRefresh size={14} />}
              >
                Retry Analysis
              </Button>
              <Button 
                size="sm" 
                variant="light" 
                color="orange" 
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('skipFailedArea', { detail: { areaName } }));
                }}
              >
                Skip Area
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Security Assessment Card - Shows security threat assessment progress
 * Now displayed as 7th area in the standard flow
 */
function SecurityAssessmentCard({ status, outputs, autonomous, totalAreas, areaIndex, onRetry, onGenerateReport }) {
  const isGenerating = status.status === 'generating';
  const isCompleted = status.status === 'completed';
  const isFailed = status.status === 'failed';

  return (
    <Card
      withBorder
      shadow="md"
      radius="md"
      p="lg"
      style={{
        borderLeft: isCompleted ? '4px solid #37b24d' : isFailed ? '4px solid #e03131' : '4px solid #7c3aed'
      }}
    >
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="md">
            <Center
              style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: isCompleted ? '#d3f9d8' : isFailed ? '#ffe3e3' : '#e5dbff'
              }}
            >
              {isCompleted ? (
                <IconCheck size={24} color="#37b24d" />
              ) : isFailed ? (
                <IconAlertTriangle size={24} color="#e03131" />
              ) : (
                <IconShieldCheck size={24} color="#7c3aed" />
              )}
            </Center>

            <div>
              <Group gap="xs" align="center">
                <Text size="xl" fw={700} c={isCompleted ? 'green' : isFailed ? 'red' : 'violet'}>
                  🛡️ Security Threat Assessment
                </Text>
                <Text size="sm" c="dimmed" fw={500}>
                  ({areaIndex}/{totalAreas})
                </Text>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                {isGenerating ? 'Analyzing security threats and generating controls...'
                  : isCompleted ? 'Security assessment complete ✓'
                  : isFailed ? 'Security assessment failed'
                  : 'Security threat analysis'}
              </Text>
            </div>
          </Group>

          {autonomous && (
            <Badge size="lg" variant="filled" color="green" leftSection="🤖">
              Autonomous
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Task Progress */}
        {isGenerating && (
          <Box>
            <Text size="sm" fw={600} mb="md">Security Tasks Progress</Text>
            <Timeline active={-1} bulletSize={20} lineWidth={2}>
              <Timeline.Item
                bullet={outputs?.scenarios ? <IconCheck size={12} /> : <AIGeneratingIcon />}
                title={<Text size="sm" fw={500}>Generating Threat Scenarios</Text>}
                color={outputs?.scenarios ? 'green' : 'violet'}
              >
                {outputs?.scenarios && (
                  <Text size="xs" c="dimmed" mt={4}>
                    ✓ Generated {outputs.scenarioCount || outputs.scenarios.length} threat scenarios
                  </Text>
                )}
                {status.currentTask?.includes('scenarios') && (
                  <Text size="xs" c="violet" mt={4}>
                    🔄 AI analyzing model threats...
                  </Text>
                )}
              </Timeline.Item>

              <Timeline.Item
                bullet={outputs?.evaluatedScenarios?.length > 0 ? <IconCheck size={12} /> : <AIGeneratingIcon />}
                title={<Text size="sm" fw={500}>Assessing Threat Levels</Text>}
                color={outputs?.evaluatedScenarios?.length > 0 ? 'green' : outputs?.scenarios ? 'violet' : 'gray'}
              >
                {outputs?.evaluatedScenarios?.length > 0 && (
                  <Text size="xs" c="dimmed" mt={4}>
                    ✓ Assessed {outputs.evaluatedScenarios.length} scenarios • {outputs.progress || ''}
                  </Text>
                )}
                {status.currentTask?.includes('threats') && outputs?.currentScenario && (
                  <Text size="xs" c="violet" mt={4}>
                    🔄 Evaluating: {outputs.currentScenario}
                  </Text>
                )}
              </Timeline.Item>

              <Timeline.Item
                bullet={outputs?.totalControls > 0 ? <IconCheck size={12} /> : <AIGeneratingIcon />}
                title={<Text size="sm" fw={500}>Generating Security Controls</Text>}
                color={outputs?.totalControls > 0 ? 'green' : outputs?.evaluatedScenarios?.length > 0 ? 'violet' : 'gray'}
              >
                {outputs?.totalControls > 0 && (
                  <Text size="xs" c="dimmed" mt={4}>
                    ✓ Generated {outputs.totalControls} mitigation controls
                  </Text>
                )}
                {status.currentTask?.includes('controls') && (
                  <Text size="xs" c="violet" mt={4}>
                    🔄 Creating security controls...
                  </Text>
                )}
              </Timeline.Item>
            </Timeline>
          </Box>
        )}

        {/* Results - Show scenario details */}
        {outputs?.scenarios && outputs.scenarios.length > 0 && (
          <Box>
            <Divider my="sm" />
            <Text size="sm" fw={600} c="violet" mb="md">
              🎯 Identified Threat Scenarios
            </Text>

            <Stack gap="sm">
              {outputs.scenarios.slice(0, 3).map((scenario, idx) => {
                const evaluation = outputs.evaluatedScenarios?.find(e => e.scenarioName === scenario.name);
                const controls = outputs.generatedControls?.find(c => c.scenarioName === scenario.name);

                return (
                  <Box key={idx} p="md" bg="violet.0" style={{ borderRadius: 8, borderLeft: '3px solid #7c3aed' }}>
                    <Group justify="space-between" align="flex-start" mb="xs">
                      <Text size="sm" fw={600} c="dark">
                        {scenario.name || `Scenario ${idx + 1}`}
                      </Text>
                      {evaluation && (
                        <Badge size="sm" variant="filled" color={
                          evaluation.threatLevel === 'Critical' ? 'red' :
                          evaluation.threatLevel === 'High' ? 'orange' :
                          evaluation.threatLevel === 'Moderate' ? 'yellow' : 'blue'
                        }>
                          {evaluation.threatLevel}
                        </Badge>
                      )}
                    </Group>

                    <Text size="xs" c="dimmed" mb="xs" lineClamp={2}>
                      {scenario.description}
                    </Text>

                    {evaluation && (
                      <Group gap="xs" mt="xs">
                        <Badge size="xs" variant="light" color="orange">
                          Likelihood: {evaluation.likelihood}
                        </Badge>
                        <Badge size="xs" variant="light" color="red">
                          Severity: {evaluation.severity}
                        </Badge>
                        {controls && (
                          <Badge size="xs" variant="light" color="green">
                            {controls.controlsCount} Controls
                          </Badge>
                        )}
                      </Group>
                    )}
                  </Box>
                );
              })}

              {outputs.scenarios.length > 3 && (
                <Text size="xs" c="dimmed" ta="center">
                  +{outputs.scenarios.length - 3} more scenarios assessed
                </Text>
              )}
            </Stack>
          </Box>
        )}

        {/* Completion Summary */}
        {isCompleted && status.results && (
          <Box p="md" bg="green.0" style={{ borderRadius: 8 }}>
            <Group gap="md" align="center">
              <IconCheck size={20} color="#37b24d" />
              <div>
                <Text size="sm" fw={600} c="green">
                  Security Assessment Complete
                </Text>
                <Text size="xs" c="dimmed">
                  {status.results.scenarios} threat scenarios identified • {status.results.totalControls} security controls generated
                </Text>
              </div>
            </Group>
          </Box>
        )}

        {/* Error State */}
        {isFailed && (
          <>
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              <Text size="sm" fw={500}>Security assessment encountered an error</Text>
              <Text size="xs" c="dimmed" mt={4}>
                {status.error || 'Unknown error occurred'}
              </Text>
            </Alert>

            {/* Action Buttons for Failed State */}
            <Group justify="center" gap="sm">
              <Button
                size="sm"
                variant="outline"
                onClick={onRetry}
                leftSection={<IconRefresh size={14} />}
              >
                Retry Security Assessment
              </Button>
              <Button
                size="sm"
                variant="light"
                color="green"
                onClick={onGenerateReport}
                leftSection={<IconCheck size={14} />}
              >
                Skip and Complete Assessment
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Card>
  );
}


